import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  countRowsCreatedAfter,
  emptySuperuserNotificationCounts,
  getSuperuserSeenStorageKey,
  queryLatestSuperuserContactSubmissions,
  queryLatestSuperuserFeedback,
  queryLatestSuperuserOrganisations,
  queryLatestSuperuserUsers,
  readSuperuserSeenAt,
  type SuperuserNotificationCounts,
  type SuperuserNotificationTabId,
} from '@/features/superuser/data/superuserNotifications';
import { queryKeys } from '@/shared/hooks';

const notificationRefetchIntervalMs = 60_000;

const notificationTabIds: readonly SuperuserNotificationTabId[] = [
  'users',
  'organisations',
  'feedback',
  'contact-submissions',
];

type SuperuserNotificationCountsResult = {
  counts: SuperuserNotificationCounts;
  total: number;
};

const asRows = <TRow>(rows: TRow[] | undefined): TRow[] => (Array.isArray(rows) ? rows : []);

const initialiseMissingSeenTimestamps = (userId: string | null | undefined): void => {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  notificationTabIds.forEach((tabId) => {
    const key = getSuperuserSeenStorageKey(userId, tabId);
    if (window.localStorage.getItem(key) === null) {
      window.localStorage.setItem(key, String(now));
    }
  });
};

export const useSuperuserNotificationCounts = (
  userId: string | null | undefined,
  enabled: boolean,
): SuperuserNotificationCountsResult => {
  const queryClient = useQueryClient();
  const [seenRevision, setSeenRevision] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    initialiseMissingSeenTimestamps(userId);
    setSeenRevision((current) => current + 1);
  }, [enabled, userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleSeenChange = (): void => {
      setSeenRevision((current) => current + 1);
      void queryClient.invalidateQueries({ queryKey: queryKeys.superuserNotifications(userId) });
    };
    window.addEventListener('flowqa-superuser-notifications-seen', handleSeenChange);
    return () => {
      window.removeEventListener('flowqa-superuser-notifications-seen', handleSeenChange);
    };
  }, [queryClient, userId]);

  const usersQuery = useQuery({
    queryKey: queryKeys.superuserNotificationUsers(userId),
    queryFn: queryLatestSuperuserUsers,
    enabled,
    refetchInterval: notificationRefetchIntervalMs,
  });
  const organisationsQuery = useQuery({
    queryKey: queryKeys.superuserNotificationOrganisations(userId),
    queryFn: queryLatestSuperuserOrganisations,
    enabled,
    refetchInterval: notificationRefetchIntervalMs,
  });
  const feedbackQuery = useQuery({
    queryKey: queryKeys.superuserNotificationFeedback(userId),
    queryFn: queryLatestSuperuserFeedback,
    enabled,
    refetchInterval: notificationRefetchIntervalMs,
  });
  const contactSubmissionsQuery = useQuery({
    queryKey: queryKeys.superuserNotificationContactSubmissions(userId),
    queryFn: queryLatestSuperuserContactSubmissions,
    enabled,
    refetchInterval: notificationRefetchIntervalMs,
  });

  return useMemo(() => {
    if (!enabled) {
      return { counts: emptySuperuserNotificationCounts(), total: 0 };
    }
    const counts: SuperuserNotificationCounts = {
      users: countRowsCreatedAfter(asRows(usersQuery.data), readSuperuserSeenAt(userId, 'users')),
      organisations: countRowsCreatedAfter(
        asRows(organisationsQuery.data),
        readSuperuserSeenAt(userId, 'organisations'),
      ),
      feedback: countRowsCreatedAfter(
        asRows(feedbackQuery.data),
        readSuperuserSeenAt(userId, 'feedback'),
      ),
      'contact-submissions': countRowsCreatedAfter(
        asRows(contactSubmissionsQuery.data),
        readSuperuserSeenAt(userId, 'contact-submissions'),
      ),
    };
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    return { counts, total };
  }, [
    contactSubmissionsQuery.data,
    enabled,
    feedbackQuery.data,
    organisationsQuery.data,
    seenRevision,
    userId,
    usersQuery.data,
  ]);
};
